import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import LoadingButton from '@mui/lab/LoadingButton'
import {Box, Button, FormControl, IconButton, InputAdornment, InputBase} from '@mui/material'
import {Typography} from '@viptech/react-components'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import AppVersion from 'src/components/AppVersion'
import {useAuth} from 'src/contexts/AuthContext'
import useLoading from 'src/hooks/useLoading'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import api from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {isPasswordLoginValid} from 'src/utilities/regexValidation'
import {useToggle} from 'usehooks-ts'
import BrainLoginThumb from '../../images/brain-login-image.png'
import BrainLoginVideo from '../../images/brain-login-video.mp4'
import {InitialRoutesWithPermissions} from '../../routes/RoutesPageFunctions'
import {BlendDiv, VideoContainer} from '../login/Styles'
import {ButtonDiv, Container, Form, InputDiv, LoginBox, Logo, ScreenContainer, TopDiv} from './Styles'

function ChangePassword() {
  const [password, setPassword] = useState<string>('')
  const [passwordConfirmed, setPasswordConfirmed] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [lastPassword, setLastPassword] = useState<string>('')

  const [isConfirmed, setIsConfirmed] = useState<boolean>(true)
  const [lastShowPassword, setLastShowPassword] = useToggle(false)
  const [showPassword, setShowPassword] = useToggle(false)
  const [confirmShowPassword, setConfirmShowPassword] = useToggle(false)

  const {loginPagesLogo} = usePersonalizedThemeByCompany()

  const {user, login, logout} = useAuth()
  const {loading, applyLoading} = useLoading(false)
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()

  const forcePassword = user.forcePasswordUpdate

  const handleChange = (e: any) => {
    setPasswordConfirmed(e.target.value)
  }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  async function UpdatePassword() {
    if (!user?.id) throw new Error('Missing id when editing user')
    try {
      await api.auth.updatePassword({
        newPassword: passwordConfirmed,
        currentPassword: lastPassword,
      })

      await applyLoading(() => login(user.email, passwordConfirmed, false))
      navigate(`/${InitialRoutesWithPermissions(user).path}`)
    } catch (err) {
      const error = err as AxiosError
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao alterar a senha')
    }
  }

  useEffect(() => {
    if (!lastPassword) return

    if (!isPasswordLoginValid(lastPassword)) {
      setIsConfirmed(false)
      setMessage(
        'A senha atual deve conter no mínimo 8 e no máximo 40 caracteres, e deve conter um número e uma letra. Caso sua senha não atenda estes requisitos, solicite uma alteração junto ao seu administrador',
      )
      return
    }

    if (!password) {
      setIsConfirmed(true)
      setPasswordConfirmed('')
      return
    }

    if (!isPasswordLoginValid(password)) {
      setIsConfirmed(false)
      setMessage('A senha deve conter no mínimo 8 e no máximo 40 caracteres, e deve conter um número e uma letra')
    } else {
      setIsConfirmed(true)
      if (passwordConfirmed !== password) {
        setIsConfirmed(false)
        setMessage('A senha digitada está diferente')
      } else {
        setIsConfirmed(true)
      }
    }
  }, [password, passwordConfirmed, lastPassword])

  return (
    <ScreenContainer>
      <BlendDiv />
      <Container>
        <LoginBox>
          <TopDiv>
            <Logo src={loginPagesLogo} />
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px', alignSelf: 'flex-start'}}>
              <Typography variant='label' color={'#F2F4F7'} size='22px'>
                Recuperar senha
              </Typography>
              <Typography variant='label' size='16px' color='#EAECF0'>
                Olá {user?.name}, digite sua nova senha
              </Typography>
            </Box>
          </TopDiv>
          <Form style={{gap: '24px'}}>
            <InputDiv>
              <FormControl>
                <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                  <Typography variant='label' htmlFor='old-password' color='#F2F4F7' size='16px' family='Inter'>
                    Senha atual
                  </Typography>

                  <InputBase
                    id='old-password'
                    type={lastShowPassword ? 'text' : 'password'}
                    value={lastPassword}
                    required
                    sx={styles.inputPassword}
                    autoFocus={true}
                    onKeyPress={(event) => false}
                    onChange={(e) => setLastPassword(e.target.value)}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          sx={{color: '#FFFFFF'}}
                          aria-label='toggle password visibility'
                          onClick={() => setLastShowPassword()}
                          onMouseDown={handleMouseDownPassword}
                          edge='end'>
                          {lastShowPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Box>
              </FormControl>
            </InputDiv>
            <InputDiv>
              <FormControl>
                <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                  <Typography variant='label' htmlFor='new-password' color='#F2F4F7' size='16px' family='Inter'>
                    Nova senha
                  </Typography>
                  <InputBase
                    id='new-password'
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    sx={styles.inputPassword}
                    autoFocus={true}
                    onKeyPress={(event) => false}
                    onChange={(e) => {
                      setPassword(e.target.value)
                    }}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          sx={{color: '#FFFFFF'}}
                          aria-label='toggle password visibility'
                          onClick={() => setShowPassword()}
                          onMouseDown={handleMouseDownPassword}
                          edge='end'>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Box>
              </FormControl>
            </InputDiv>
            <InputDiv>
              <FormControl>
                <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                  <Typography variant='label' htmlFor='confirm-password' color='#F2F4F7' size='16px' family='Inter'>
                    Confirme sua senha
                  </Typography>

                  <InputBase
                    id='confirm-password'
                    type={confirmShowPassword ? 'text' : 'password'}
                    value={passwordConfirmed}
                    sx={styles.inputPassword}
                    autoFocus={true}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position='end'>
                        <IconButton
                          sx={{color: '#FFFFFF'}}
                          aria-label='toggle password visibility'
                          onClick={() => setConfirmShowPassword()}
                          onMouseDown={handleMouseDownPassword}
                          edge='end'>
                          {confirmShowPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Box>
              </FormControl>
            </InputDiv>
            {message && (
              <InputDiv>
                <Typography variant='label' hidden={isConfirmed} color='tomato' size='14px' weight={700}>
                  {message}
                </Typography>
              </InputDiv>
            )}
            <ButtonDiv>
              <LoadingButton
                onClick={() => UpdatePassword()}
                variant='contained'
                sx={styles.confirmButton}
                disabled={!isConfirmed || !lastPassword || !password}
                loading={loading}>
                Confirmar
              </LoadingButton>
              <Button onClick={() => (forcePassword ? logout() : navigate(-1))} variant='contained' sx={styles.cancelButton}>
                {forcePassword ? 'Sair' : 'Voltar'}
              </Button>
            </ButtonDiv>
          </Form>
        </LoginBox>
      </Container>
      <AppVersion color='white' />
      <VideoContainer muted autoPlay loop preload='auto' src={BrainLoginVideo} poster={BrainLoginThumb}></VideoContainer>
    </ScreenContainer>
  )
}

const styles = createStyleSheet({
  confirmButton: {
    width: '100%',
    height: '50px',
    fontFamily: 'Inter',
    fontSize: '16px',
    boxShadow: 'none',
    textTransform: 'none',
    marginBottom: '15px',
    borderRadius: '12px',
    '&:disabled': {
      background: '#98A2B3',
      color: '#FFFFFF',
    },
  },
  inputPassword: {
    fontSize: '16px',
    color: '#ffffff',
    backgroundColor: '#344054',
    height: '3.5em',
    borderRadius: '10px',
    padding: '0px 15px 0px 15px',
    border: 'none',
    textIndent: '4%',
    outline: '0',
    '&:focus': {
      outline: 'none',
    },
  },
  cancelButton: {
    width: '100%',
    height: '50px',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: 'Inter',
    fontWeight: 400,
    boxShadow: 'none',
    textTransform: 'none',
    marginRight: '15px',
    background: 'transparent',
    border: '1px solid #98A2B3',
    '&:hover': {
      background: '#ffffff20',
    },
  },
})

export default ChangePassword
